import { useNavigate, useParams } from "react-router-dom";
import { useAtom, useAtomValue } from "jotai";
import { useMemo, useState } from "react";
import { Button } from "reactstrap";

import EditWorkspaceDrawer from "../../components/Drawers/EditWorkspaceDrawer/EditWorkspaceDrawer";
import useUpdateWorkspaceSettings from "../../api/api-hooks/workspaces/useUpdateWorkspaceSettings";
import useJoinWorkspace from "../../api/api-hooks/workspaces/useJoinWorkspace";
import useWorkspacePermissions from "../../hooks/useWorkspacePermissions";
import useWorkspaces from "../../api/api-hooks/workspaces/useWorkspaces";
import useCloudPermissions from "../../hooks/useCloudPermissions";
import AppHelmet from "../../components/AppComponents/AppHelmet";
import { Workspace } from "../../types/WorkspaceInterfaces";
import SkeletonLoader from "../../elements/SkeletonLoader";
import AppEmptyState from "../../elements/AppEmptyState";
import { tenantIDAtom } from "../../atoms/GlobalAtoms";
import { newToastEvent } from "../../utils/ToastUtil";
import { tenantAtom } from "../../atoms/GlobalAtoms";
import ListItem from "../../components/CRM/ListItem";
import IconButton from "../../elements/IconButton";
import useToggle from "../../hooks/useToggle";
import Constants from "../../utils/Constants";

export const JoinWorkspaceButton = ({
  workspaceID,
}: {
  workspaceID: string;
}) => {
  const tenantID = useAtomValue(tenantIDAtom);
  const [workspaceBeingJoined, setWorkspaceBeingJoined] = useState<
    string | null
  >(null);
  const { joinWorkspace, joiningWorkspace } = useJoinWorkspace();
  const { cPermissions } = useCloudPermissions();
  const navigate = useNavigate();

  const join = (e: any, workspaceID: string) => {
    e.stopPropagation();
    setWorkspaceBeingJoined(workspaceID);

    joinWorkspace({
      workspaceID: workspaceID,
      onSuccess: () => {
        setWorkspaceBeingJoined(null);
        navigate(`/clouds/${tenantID}/workspaces/${workspaceID}/home/pages`);
      },
      onSettled: () => setWorkspaceBeingJoined(null),
    });
  };

  return (
    <Button
      size="sm"
      outline
      color="primary"
      onClick={(e) => join(e, workspaceID)}
      disabled={!cPermissions.cloud_join_workspace || joiningWorkspace}
    >
      {joiningWorkspace && workspaceBeingJoined === workspaceID
        ? "Joining"
        : "Join"}
    </Button>
  );
};

const NoWorkspaces = ({ openDrawer }: { openDrawer: () => void }) => {
  return (
    <AppEmptyState
      title="Create Your First Workspace"
      description="Your GrowthCloud doesn't have any workspaces. Get started by creating
        your first one and adding your team members to it."
      buttonTitle="Get Started"
      onClick={openDrawer}
    />
  );
};

const WorkspacesList = ({
  wspaces,
}: {
  wspaces: (Workspace & { isMember?: boolean })[];
}) => {
  const { updateSettings, updateSubmitting } = useUpdateWorkspaceSettings(true);
  const { cPermissions } = useCloudPermissions();
  const { tenantID } = useParams();

  const filteredWorkspaces = useMemo(() => {
    if (cPermissions?.cloud_modify_workspace) return wspaces;

    return wspaces.filter((wspace) => !wspace?.Hide);
  }, [wspaces]);

  return (
    <div style={{ width: "100%", maxWidth: 400 }}>
      {filteredWorkspaces.length > 0
        ? filteredWorkspaces.map((wspace) => {
            return (
              <WorkspaceListItem
                key={wspace.id}
                wspace={wspace}
                tenantID={tenantID || ""}
                updateSubmitting={updateSubmitting}
                cPermissions={cPermissions}
                saveSettings={saveSettings}
              />
            );
          })
        : "None."}
    </div>
  );

  function saveSettings(wspace: Workspace, key: "Hide" | "Default") {
    // To avoid setting a workspace as defualt when the Hide option is enabled.
    if (key === "Hide" && !!wspace?.Default) {
      newToastEvent("warning", "You can't hide a default wokspace.");
      return;
    }

    if (key === "Default" && !!wspace.Hide) {
      newToastEvent("warning", "You can't save a hidden workspace as default.");
      return;
    }

    updateSettings({
      workspaceID: wspace?.id!,
      payload: { [key]: !wspace?.[key] },
    });
  }
};

const WorkspaceListItem = ({
  wspace,
  tenantID,
  updateSubmitting,
  cPermissions,
  saveSettings,
}: {
  wspace: Workspace & { isMember?: boolean };
  tenantID: string;
  updateSubmitting: boolean;
  cPermissions: any;
  saveSettings: (wspace: Workspace, key: "Hide" | "Default") => void;
}) => {
  const navigate = useNavigate();
  const permissions = useWorkspacePermissions(true, wspace.id);

  return (
    <ListItem
      title={wspace.Name}
      icon="mdi mdi-view-dashboard-outline"
      onClick={() =>
        navigate(`/clouds/${tenantID}/workspaces/${wspace.id}/activity`)
      }
      loading={updateSubmitting}
      optionsButton={
        <div className="d-flex ">
          {cPermissions?.cloud_modify_workspace && wspace.isMember && (
            <>
              <IconButton
                onClick={() => saveSettings(wspace, "Default")}
                icon={wspace?.Default ? "mdi mdi-star" : "mdi mdi-star-outline"}
                tooltipText={
                  wspace?.Default ? "Remove Default" : "Save as Default"
                }
                disabled={!!wspace?.Hide || tenantID === Constants.SUP_CLOUD_ID}
                color="warning"
                className="mx-1"
              />
              <IconButton
                onClick={() => saveSettings(wspace, "Hide")}
                icon={wspace?.Hide ? "eye-off" : "eye"}
                tooltipText={wspace?.Hide ? "Show Workspace" : "Hide Workspace"}
                disabled={!!wspace?.Default}
                className="mx-1"
              />
              {permissions.wPermissions.workspace_modify_workspace_settings && (
                <IconButton
                  onClick={() =>
                    navigate(
                      `/clouds/${tenantID}/workspaces/${wspace.id}/home/settings`
                    )
                  }
                  icon="mdi mdi-cog"
                  tooltipText="Settings"
                />
              )}
            </>
          )}
          {!wspace?.isMember ? (
            <>
              <JoinWorkspaceButton workspaceID={wspace?.id!} />
            </>
          ) : undefined}
        </div>
      }
    />
  );
};

const AllWorkspaces = () => {
  const {
    workspaces,
    userOwnedWorkspaces,
    userCollabWorkspaces,
    publicWorkspaces,
    workspacesLoading,
  } = useWorkspaces();
  const [currentTenant] = useAtom(tenantAtom);
  const [workspaceDrawer, toggleWorkspaceDrawer] = useToggle();

  return (
    <>
      <AppHelmet title="All Workspaces - GrowthCloud.com" />
      <div className="p-4">
        {currentTenant && (
          <div
            className="d-flex align-items-baseline mb-2"
            style={{ justifyContent: "space-between" }}
          >
            <h2>
              {currentTenant?.TenantName}
              {currentTenant?.TenantName?.slice(-1)?.toLowerCase() === "s"
                ? "'"
                : "'s"}{" "}
              Workspaces
            </h2>
            <Button
              color="success"
              onClick={toggleWorkspaceDrawer}
              disabled={currentTenant.TenantID === Constants.SUP_CLOUD_ID}
            >
              Create Workspace
            </Button>
          </div>
        )}
        <hr className="mt-2 mb-4" />
        {workspacesLoading && (
          <>
            <SkeletonLoader maxWidth={400} />
            <SkeletonLoader maxWidth={400} />
            <SkeletonLoader maxWidth={400} />
            <SkeletonLoader maxWidth={400} />
            <SkeletonLoader maxWidth={400} />
            <SkeletonLoader maxWidth={400} />
          </>
        )}
        {!workspacesLoading && workspaces?.length < 1 && (
          <NoWorkspaces openDrawer={toggleWorkspaceDrawer} />
        )}
        {userOwnedWorkspaces?.length > 0 && (
          <>
            <h4 className="mb-0">My Workspaces</h4>
            <div className="mb-3">
              <small>Workspaces that you're the owner of.</small>
            </div>
            <WorkspacesList wspaces={userOwnedWorkspaces} />
            {(userCollabWorkspaces?.length > 0 ||
              (publicWorkspaces && publicWorkspaces?.length > 0)) && <hr />}
          </>
        )}
        {userCollabWorkspaces?.length > 0 && (
          <>
            <h4 className="mb-0">Workspaces I Collaborate On</h4>
            <div className="mb-3">
              <small>Workspaces that you're a member of.</small>
            </div>
            <WorkspacesList wspaces={userCollabWorkspaces} />
            {publicWorkspaces && publicWorkspaces?.length > 0 && <hr />}
          </>
        )}
        {publicWorkspaces && publicWorkspaces?.length > 0 && (
          <>
            <h4 className="mb-0">Other Workspaces</h4>
            <div className="mb-3">
              <small>
                Workspaces in the {currentTenant?.TenantName} cloud that you're
                able to join.
              </small>
            </div>
            <WorkspacesList wspaces={publicWorkspaces} />
          </>
        )}
        <EditWorkspaceDrawer
          open={workspaceDrawer}
          handleClose={toggleWorkspaceDrawer}
        />
      </div>
    </>
  );
};

export default AllWorkspaces;
